import {
  createTranslator,
  useLocale,
  useMessages,
  type AbstractIntlMessages,
} from "next-intl";

import { intlMessageFallback } from "@/i18n/intl-handlers";
import { mergeMessages, type Messages } from "@/shared/i18n/merge-messages";

/**
 * Перевод строк интерфейса вне React-дерева (KAN-1286): валидация, сервисы,
 * zod-схемы, модульные справочники — там, где хука `useTranslations` нет.
 *
 * Словарь сюда не импортируется (это ~0,5 МБ в бандл): его кладёт
 * `NextIntlClientProvider` при старте — те же сообщения, что уже пришли
 * с сервера. Ключи — от корня `Ui`, как у `useTranslations("Ui")`.
 *
 * Сервер. Один процесс рендерит запросы на разных языках вперемешку: пока
 * запрос на /ru ждёт данных под Suspense, запрос на /en успевает перерегистрировать
 * глобальный переводчик, и строка уходила в HTML не на том языке. Поэтому во
 * время серверного рендера язык и сообщения читаются из провайдера текущего
 * запроса (это чтение контекста, а не состояние хука — порядок вызовов не важен).
 * Глобальная регистрация на сервере — только запасной путь вне рендера.
 *
 * KAN-975: словарь разбит на основной и семейства маршрутов (турниры, админка).
 * Регистраций две — корень отдаёт основной словарь, слой семейства — слияние.
 * Если корень перерендерится ПОСЛЕ слоя (гидрация, смена темы/авторизации) и
 * просто перезапишет глобальный переводчик, то до его повторной регистрации
 * `translateUi` не найдёт группы семейства и next-intl вернёт сырой ключ —
 * на экране оказалось `Ui.litTournamentScheduleTab.dayShort` вместо даты.
 * Поэтому регистрация только расширяется: словарь, который не покрывает уже
 * зарегистрированный, игнорируется, а недостающее сливается.
 *
 * Промах ключа наружу не выходит: `getMessageFallback` отдаёт последний сегмент
 * (а транслит-ключи `Ui.lit…` и бэкенд-коды — пустую строку), как в провайдере.
 */

type Values = Record<string, string | number | Date>;
type Translate = (key: string, values?: Values) => string;

const byMessages = new WeakMap<AbstractIntlMessages, Map<string, Translate>>();

function translatorFor(locale: string, messages: AbstractIntlMessages): Translate {
  let perLocale = byMessages.get(messages);
  if (!perLocale) {
    perLocale = new Map();
    byMessages.set(messages, perLocale);
  }
  let t = perLocale.get(locale);
  if (!t) {
    const raw = createTranslator({
      locale,
      messages,
      namespace: "Ui",
      // Без этого next-intl на промахе возвращает полный ключ с неймспейсом
      // («Ui.lit…») и шумит стеком в консоль.
      onError: (error) => {
        if (process.env["NODE_ENV"] !== "production") {
          console.warn("[i18n] translateUi:", error.message);
        }
      },
      getMessageFallback: ({ key }) => intlMessageFallback({ error: { code: "MISSING_MESSAGE" }, key }),
    });
    t = (key, values) => (raw as unknown as Translate)(key, values);
    perLocale.set(locale, t);
  }
  return t;
}

let registered: Translate | null = null;
let registeredMessages: AbstractIntlMessages | null = null;
let registeredLocale: string | null = null;

const isPlain = (v: unknown): v is Messages =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Покрывает ли уже зарегистрированный словарь входящий — по группам верхнего уровня
 * (`Ui.<группа>`, `Tournaments`, `Errors`, …). Погруппового сравнения достаточно:
 * словари отличаются именно набором групп, а не отдельными строками.
 */
function covers(known: AbstractIntlMessages, incoming: AbstractIntlMessages): boolean {
  const base = known as Messages;
  const next = incoming as Messages;
  for (const [key, value] of Object.entries(next)) {
    if (key === "Ui") {
      const baseUi = base["Ui"];
      if (!isPlain(value)) continue;
      if (!isPlain(baseUi)) return false;
      for (const group of Object.keys(value)) {
        if (!(group in baseUi)) return false;
      }
      continue;
    }
    if (!(key in base)) return false;
  }
  return true;
}

export function setUiMessages(locale: string, messages: AbstractIntlMessages): void {
  if (locale === registeredLocale && registeredMessages && covers(registeredMessages, messages)) return;

  registeredMessages =
    locale === registeredLocale && registeredMessages
      ? (mergeMessages(registeredMessages as Messages, messages as Messages) as AbstractIntlMessages)
      : messages;
  registeredLocale = locale;
  registered = translatorFor(locale, registeredMessages);
}

/** Переводчик провайдера текущего серверного рендера или null вне рендера. */
function renderTranslator(): Translate | null {
  try {
    // Вызывается из обычной функции намеренно: только на сервере и только
    // ради чтения контекста провайдера текущего запроса.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const locale = useLocale();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const messages = useMessages();
    return translatorFor(locale, messages);
  } catch {
    return null;
  }
}

export function translateUi(key: string, values?: Values): string {
  const t = (typeof window === "undefined" && renderTranslator()) || registered;
  // Без переводчика ключ наружу не отдаём: на экране окажется его огрызок, а не код.
  if (!t) return intlMessageFallback({ error: { code: "MISSING_MESSAGE" }, key });
  return t(key, values);
}
